import React, { createContext, useState, useEffect } from 'react';
import { User, Role } from '@/types/domain/user';
import { mockAuthProvider } from '@/services/auth/MockAuthProvider';
import { AuthResult } from '@/services/auth/IAuthProvider';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (emailOrEmployeeId: string, password?: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  switchRole: (role: Role) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    mockAuthProvider.getCurrentUser().then((currentUser) => {
      setUser(currentUser);
      setIsLoading(false);
    });
  }, []);

  const login = async (emailOrEmployeeId: string, password?: string): Promise<AuthResult> => {
    setIsLoading(true);
    const result = await mockAuthProvider.login({ emailOrEmployeeId, password });
    if (result.success && result.user) {
      setUser(result.user);
    }
    setIsLoading(false);
    return result;
  };

  const logout = async () => {
    setIsLoading(true);
    await mockAuthProvider.logout();
    setUser(null);
    setIsLoading(false);
  };

  const switchRole = async (role: Role) => {
    setIsLoading(true);
    const updatedUser = await mockAuthProvider.switchRole(role);
    setUser(updatedUser);
    setIsLoading(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        switchRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
